import type { Alert, AlertKind, AlertRule } from "@cloudserver/contracts";
import type { TFunction } from "i18next";

/*
 * How alerts and rules read in the panel (docs/alerts.md): the same facts
 * as the Telegram messages, from the panel's own strings.
 */

/** What a rule's threshold measures. */
export const UNIT: Record<AlertKind, "percent" | "per_core" | "hours" | "days" | "count" | "none"> =
  {
    agent_down: "none",
    server_off: "none",
    cpu_high: "percent",
    memory_high: "percent",
    disk_high: "percent",
    load_high: "per_core",
    disk_forecast: "hours",
    container_down: "none",
    service_down: "none",
    security_updates: "count",
    ssl_expiring: "days",
    operation_failed: "none",
    budget_threshold: "none",
  };

/** Kinds that last "for N minutes" before they fire. */
export const HAS_DURATION: ReadonlySet<AlertKind> = new Set([
  "agent_down",
  "server_off",
  "cpu_high",
  "memory_high",
  "disk_high",
  "load_high",
  "container_down",
  "service_down",
]);

/** One-off cloud events: on or off, a severity, nothing else. */
export const ONE_OFF: ReadonlySet<AlertKind> = new Set(["operation_failed", "budget_threshold"]);

const number = (value: number | null, locale: string) =>
  value === null ? "—" : new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);

/** What happened, e.g. «Память 93,4 %», «Сервис backup.service упал». */
export function alertText(t: TFunction, alert: Alert, locale: string): string {
  const values = {
    subject: alert.subject,
    value: number(alert.value, locale),
    threshold: number(alert.threshold, locale),
  };
  if (alert.kind === "ssl_expiring" && (alert.value ?? 1) <= 0)
    return t("alerts.text.sslExpired", values);
  return t(`alerts.text.${alert.kind}`, values);
}

/** The rule's condition, e.g. «выше 90 % дольше 10 мин», or «сразу». */
export function conditionText(
  t: TFunction,
  rule: Pick<AlertRule, "kind" | "threshold" | "durationMinutes">,
  locale: string,
): string {
  const unit = UNIT[rule.kind];
  const parts: string[] = [];
  if (unit !== "none" && rule.threshold !== null)
    parts.push(t(`alerts.condition.${unit}`, { threshold: number(rule.threshold, locale) }));
  if (ONE_OFF.has(rule.kind)) parts.push(t("alerts.condition.event"));
  else if (HAS_DURATION.has(rule.kind) && rule.durationMinutes)
    parts.push(t("alerts.condition.for", { minutes: rule.durationMinutes }));
  else parts.push(t("alerts.condition.atOnce"));
  return parts.join(" ");
}

export const SEVERITY_TONE = { warn: "warn", critical: "danger" } as const;
